import React, { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getContainerData, SaveApiResMessage } from '@/actions';
import { buildEmptyDroppedAppointmentsUrlFromGetContainerResponse } from '@/screens/Autobook/planner/liveUnloadPlannerApi';
import IconButton from '@/ctComponents/IconButton';
import { fTimeDateUSFormat } from '@/actions/Helper';
import { PlannerAvailabilityTimeline } from './PlannerAvailabilityTimeline';
import { PlannerCardHeader } from './PlannerCardHeader';
import { PlannerCardKvColumn } from './PlannerCardKvColumn';
import { PlannerCardMovementPath } from './PlannerCardMovementPath';
import styles from './LiveUnloadPlannerCard.module.css';

const STRIP_MILESTONE_LABELS = new Set(['status', 'created', 'updated']);

function normalizeContainerNo(v) {
  return String(v ?? '')
    .replace(/\s+/g, '')
    .toUpperCase();
}

/** Match planner URL / API card fields so the Appointments button still shows if only KV lists hold the number. */
function collectCardContainerNumbers(card) {
  const out = new Set();
  for (const n of card.searchContainers || []) {
    if (n != null && String(n).trim() !== '') out.add(normalizeContainerNo(n));
  }
  const pushRows = (rows) => {
    for (const row of rows || []) {
      const lab = String(row?.label || '').trim().toLowerCase();
      if (lab === 'container' && row?.value != null) {
        out.add(normalizeContainerNo(row.value));
      }
    }
  };
  pushRows(card.containerInfo);
  pushRows(card.milestones);
  if (Array.isArray(card.columnGroups)) {
    for (const g of card.columnGroups) {
      pushRows(g?.rows);
    }
  }
  return out;
}

function stripMetaFromMilestoneRows(rows) {
  return (rows || []).filter((row) => {
    const k = String(row?.label || '').trim().toLowerCase();
    return !STRIP_MILESTONE_LABELS.has(k);
  });
}

function getColumnGroups(card) {
  if (Array.isArray(card.columnGroups) && card.columnGroups.length > 0) {
    return card.columnGroups
      .filter((g) => g?.title !== 'Booking Result')
      .map((g) =>
        String(g?.title || '').trim() === 'Planner Milestones'
          ? { ...g, rows: stripMetaFromMilestoneRows(g.rows) }
          : g
      );
  }
  return [
    { title: 'Container Info', rows: card.containerInfo || [] },
    {
      title: 'Planner Milestones',
      rows: stripMetaFromMilestoneRows(card.milestones || []),
    },
  ];
}

export function LiveUnloadPlannerCard({
  card,
  returnAppointmentHref,
  filterContainerName,
}) {
  const history = useHistory();
  const timelineRef = useRef(null);
  const [activeTimelineItem, setActiveTimelineItem] = useState(null);
  const columnGroups = getColumnGroups(card);
  const kvColCount = Math.max(1, columnGroups.length);

  const createdText =
    fTimeDateUSFormat(card.createdAtIso) || card.createdAtFormatted || '';
  const updatedText =
    fTimeDateUSFormat(card.updatedAtIso) || card.updatedAtFormatted || '';

  const timelineFallbackStart = card.timeline?.windowStartLabel || '';
  const timelineFallbackEnd = card.timeline?.windowEndLabel || '';
  /** Same row as before under the track: API Created/Updated when present, else window labels (mock). */
  const axisLeft =
    createdText !== ''
      ? `Created ${createdText}`
      : timelineFallbackStart;
  const axisRight =
    updatedText !== ''
      ? `Updated ${updatedText}`
      : timelineFallbackEnd;

  const matchesFilter =
    Boolean(filterContainerName && String(filterContainerName).trim()) &&
    collectCardContainerNumbers(card).has(
      normalizeContainerNo(filterContainerName)
    );

  const fromInventoryWithReturn = Boolean(returnAppointmentHref);

  const handleReturnAppointment = () => {
    void (async () => {
      if (fromInventoryWithReturn && matchesFilter && returnAppointmentHref) {
        history.push(returnAppointmentHref);
        return;
      }
      const containerId = card.returnNavContainerId;
      if (containerId == null) {
        SaveApiResMessage({
          title: 'Return Appointments',
          message: 'Missing container id for this card.',
          mode: 'warning',
        });
        return;
      }
      try {
        const res = await getContainerData(containerId);
        if (res?.message === 'Container does not exist!') {
          history.push('/inventory/empty/dropped');
          return;
        }
        const href =
          buildEmptyDroppedAppointmentsUrlFromGetContainerResponse(res);
        if (href) {
          history.push(href);
        } else {
          SaveApiResMessage({
            title: 'Return Appointments',
            message:
              'Could not build the appointments link from container data.',
            mode: 'warning',
          });
        }
      } catch (err) {
        SaveApiResMessage({
          title: 'Error',
          message: String(err?.message || err),
          mode: 'warning',
        });
      }
    })();
  };

  return (
    <article className={styles.card}>
      <div className={styles.cardShell}>
        <div className={styles.cardLeft}>
          <PlannerCardHeader
            headerHint={card.headerHint}
            showReturnAppointment
            onReturnAppointmentClick={handleReturnAppointment}
          />

          <div className={styles.gridAndNavRow}>
            <div
              className={`${styles.gridKv} ${
                kvColCount === 2 ? styles.gridKvTwoCols : ''
              }`}
              style={{ '--kv-cols': kvColCount }}>
              {columnGroups.map((col, idx) => (
                <PlannerCardKvColumn
                  key={`${col.title}-${idx}`}
                  title={col.title}
                  rows={col.rows || []}
                  trackingUrl={card.trackingUrl}
                />
              ))}
            </div>
            <div className={styles.timelineNavExternal}>
              <IconButton
                text="Prev"
                icon="leftArrow"
                color="#131517"
                border="1px solid #d7dde8"
                onClick={() => timelineRef.current?.goPrev?.()}
              />
              <div className={styles.timelineNavTitle}>
                {activeTimelineItem
                  ? `${activeTimelineItem.title} · ${activeTimelineItem.type}`
                  : 'Item'}
              </div>
              <IconButton
                text="Next"
                icon="rightArrow"
                color="#131517"
                border="1px solid #d7dde8"
                onClick={() => timelineRef.current?.goNext?.()}
              />
            </div>
          </div>

          <div className={styles.timelineWrap}>
            <PlannerAvailabilityTimeline
              ref={timelineRef}
              windowStartLabel={axisLeft}
              windowEndLabel={axisRight}
              nodes={card.timeline?.nodes || []}
              onActiveNavItemChange={setActiveTimelineItem}
            />
          </div>

          <PlannerCardMovementPath
            movementSegments={card.movementSegments}
            movementPath={card.movementPath}
          />
        </div>
      </div>
    </article>
  );
}

export {
  PlannerCardHeader,
  PLANNER_CARD_DEFAULT_HEADER_HINT,
} from './PlannerCardHeader';
export { PlannerCardKvColumn } from './PlannerCardKvColumn';
export { PlannerCardMovementPath } from './PlannerCardMovementPath';
export { PlannerAvailabilityTimeline } from './PlannerAvailabilityTimeline';
